import React, { useState, useEffect, useCallback } from 'react'

import { colors } from 'Constants'

import { toast } from 'react-toastify'
import { useInput, runOnEnter, useDebounce } from 'Helpers'
import { toastWithErrorMessage } from 'Helpers/toast'

import Input, { AutoCompleteTags } from 'Components/shared/Input'
import { Button } from 'Components/shared/Button'
import { ErrorText } from 'Components/shared/Typography'
import { Flex } from 'lese'

import VirtualOfficesService from 'Services/virtual-offices'
import InfoService from 'Services/info'

import { createModal, closeModal } from 'Components/Modal'
import { ConfirmationModalStrict } from 'Components/Modal/ConfirmationModal'

export default ({ virtualOffice, setVirtualOffice }) => {
  const [errorText, setErrorText] = useState()

  const [suggestions, setSuggestions] = useState({})
  const [offices, setOffices] = useState([])

  const updateOffice = useDebounce(
    useCallback(
      title =>
        title &&
        VirtualOfficesService.update({
          virtualOfficeId: virtualOffice.id,
          title
        })
          .then(() => toast.success('Successfully update title'))
          .catch(toastWithErrorMessage('Failed to update title')),
      [virtualOffice]
    ),
    1000
  )

  const titleHook = useInput(virtualOffice.title, updateOffice)
  const officeHook = useInput('')
  const [officeTags, setOfficeTags] = useState([])
  const appCodeHook = useInput('')
  const [appCodeTags, setAppCodeTags] = useState([])

  useEffect(async () => {
    const retrievedOffices = await InfoService.listOffices().catch(
      toastWithErrorMessage('Failed to get offices')
    )
    setOffices(retrievedOffices)
    setSuggestions({
      appCodes: Array.from(new Set(retrievedOffices.map(office => office.appCode))),
      officeTitles: retrievedOffices.map(office => office.title)
    })
  }, [])

  useEffect(() => {
    setAppCodeTags(virtualOffice.appCodes.map(appCode => ({ text: appCode, value: appCode })))
    setOfficeTags(virtualOffice.offices.map(office => ({ text: office.title, value: office })))
  }, [virtualOffice])

  const addAppCode = async () => {
    if (!suggestions.appCodes.includes(appCodeHook.value))
      return setErrorText('The app code was not found')
    if (appCodeTags.find(appCodeTag => appCodeTag.text === appCodeHook.value))
      return setErrorText('The app code has already been added')
    setErrorText()

    const res = await VirtualOfficesService.addAppCodes({
      virtualOfficeId: virtualOffice.id,
      appCodes: [appCodeHook.value]
    }).catch(err => setErrorText(err.message))
    if (!res) return

    setAppCodeTags([
      ...appCodeTags,
      {
        text: appCodeHook.value,
        value: appCodeHook.value
      }
    ])
    appCodeHook.reset()
  }

  const addOfficeId = async () => {
    if (!suggestions.officeTitles.includes(officeHook.value))
      return setErrorText('The office was not found')
    if (officeTags.find(officeTag => officeTag.text === officeHook.value))
      return setErrorText('The office has already been added')
    setErrorText()

    const res = await VirtualOfficesService.addOfficeIds({
      virtualOfficeId: virtualOffice.id,
      officeIds: [offices.find(office => office.title === officeHook.value).id]
    }).catch(err => setErrorText(err.message))
    if (!res) return

    setOfficeTags([
      ...officeTags,
      {
        text: officeHook.value,
        value: offices.find(office => office.title === officeHook.value)
      }
    ])
    officeHook.reset()
  }

  const removeAppCode = async i => {
    const appCode = appCodeTags[i].value

    const res = await VirtualOfficesService.removeAppCodes({
      virtualOfficeId: virtualOffice.id,
      appCodes: [appCode]
    }).catch(err => setErrorText(err.message))
    if (!res) return

    appCodeTags.splice(i, 1)
    setAppCodeTags([...appCodeTags])
  }

  const removeOfficeId = async i => {
    const officeId = officeTags[i].value.id

    const res = await VirtualOfficesService.removeOfficeIds({
      virtualOfficeId: virtualOffice.id,
      officeIds: [officeId]
    }).catch(err => setErrorText(err.message))
    if (!res) return

    officeTags.splice(i, 1)
    setOfficeTags([...officeTags])
  }

  return (
    <Flex column separation="24px" xAlign="center">
      <Input placeholder="Title" {...titleHook.bind} tabIndex={0} />
      <AutoCompleteTags
        tags={officeTags}
        removeTag={removeOfficeId}
        {...officeHook.bind}
        onKeyDown={runOnEnter(addOfficeId)}
        suggestions={suggestions.officeTitles}
        placeholder="Provider Offices"
      />
      <AutoCompleteTags
        tags={appCodeTags}
        removeTag={removeAppCode}
        {...appCodeHook.bind}
        onKeyDown={runOnEnter(addAppCode)}
        suggestions={suggestions.appCodes}
        placeholder="App Codes"
      />

      {errorText && <ErrorText>{errorText}</ErrorText>}
      <Flex separation="24px">
        <Button
          color={colors.redMuted}
          onClick={() =>
            createModal(
              <ConfirmationModalStrict
                requiredText={virtualOffice.title}
                description={
                  <>
                    Are you sure you want to {virtualOffice?.archived ? 'unarchive' : 'archive'}{' '}
                    this virtual office?
                    <br />
                    This process is reversable.
                  </>
                }
                onConfirm={() => {
                  VirtualOfficesService.update({
                    virtualOfficeId: virtualOffice.id,
                    archived: !virtualOffice.archived
                  })
                    .then(() => {
                      closeModal()
                      setVirtualOffice({ ...virtualOffice, archived: !virtualOffice.archived })
                    })
                    .catch(
                      toastWithErrorMessage(
                        `Failed to ${
                          virtualOffice?.archived ? 'unarchive' : 'archive'
                        } virtual office`
                      )
                    )
                }}
              />
            )
          }
        >
          {virtualOffice?.archived ? 'Unarchive' : 'Archive'} Virtual Office
        </Button>
        <Button
          color={colors.redMuted}
          disabled
          onClick={() =>
            createModal(
              <ConfirmationModalStrict
                requiredText={virtualOffice.title}
                description={
                  <>
                    Are you sure you want to delete this virtual office?
                    <br />
                    This process is not reversable.
                  </>
                }
                onConfirm={closeModal}
                onCancel={closeModal}
              />
            )
          }
        >
          Delete Virtual Office
        </Button>
      </Flex>
    </Flex>
  )
}
